import React, { useState, useEffect } from 'react'
import { X, TrendingUp, Calendar, DollarSign, Coins } from 'lucide-react'
import { tokenDashboardAPI } from '../services/tokenAPI'
import { TokenVestingSchedule, TokenFinancialData } from '../types/token'

interface TokenDataModalProps {
  projectId: number
  projectName: string
  isOpen: boolean
  onClose: () => void
}

const TokenDataModal: React.FC<TokenDataModalProps> = ({
  projectId,
  projectName,
  isOpen,
  onClose
}) => {
  const [tokenData, setTokenData] = useState<{
    vestingSchedule: TokenVestingSchedule | null
    financialData: TokenFinancialData | null
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && projectId) {
      loadTokenData()
    }
  }, [isOpen, projectId])

  const loadTokenData = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await tokenDashboardAPI.getProjectTokenData(projectId.toString())
      if (response.success && response.data) {
        setTokenData(response.data)
      } else {
        setError('Failed to load token data')
      }
    } catch (err) {
      setError('Error loading token data')
      console.error('Error loading token data:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-lg p-6 w-full max-w-4xl mx-4 border border-gray-700 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Coins className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">Token Data - {projectName}</h3>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-300">Loading token data...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {tokenData && !loading && (
          <div className="space-y-6">
            {/* Vesting Schedule */}
            {tokenData.vestingSchedule && (
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center space-x-2 mb-4">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  <h4 className="text-lg font-semibold text-white">Vesting Schedule</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">TGE Date</label>
                    <p className="text-white">{tokenData.vestingSchedule.tge_date || 'TBD'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Next Vest Date</label>
                    <p className="text-white">{tokenData.vestingSchedule.next_vest_date || 'TBD'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Day 1 Unlock</label>
                    <p className="text-white">{tokenData.vestingSchedule.day_1_unlock ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Material Transaction</label>
                    <p className="text-white">{tokenData.vestingSchedule.material_transaction_amount ? 'Yes' : 'No'}</p>
                  </div>
                  {tokenData.vestingSchedule.wallet_owner && (
                    <div className="md:col-span-2">
                      <label className="text-sm text-gray-400">Wallet Owner</label>
                      <p className="text-white">{tokenData.vestingSchedule.wallet_owner}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Financial Data */}
            {tokenData.financialData && (
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center space-x-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  <h4 className="text-lg font-semibold text-white">Financial Analysis</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">Monthly Cash Retainer</label>
                    <p className="text-white">${tokenData.financialData.monthly_cash_retainer?.toLocaleString() || '0'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Token Percentage</label>
                    <p className="text-white">{tokenData.financialData.token_percentage ? `${(tokenData.financialData.token_percentage * 100).toFixed(2)}%` : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">TGE Date</label>
                    <p className="text-white">{tokenData.financialData.tge_date || 'TBD'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Lock-up Period</label>
                    <p className="text-white">{tokenData.financialData.lock_up_months ? `${tokenData.financialData.lock_up_months} months` : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Vesting Period</label>
                    <p className="text-white">{tokenData.financialData.vesting_months ? `${tokenData.financialData.vesting_months} months` : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">TGE Unlock %</label>
                    <p className="text-white">{tokenData.financialData.tge_unlock_percentage ? `${tokenData.financialData.tge_unlock_percentage}%` : 'N/A'}</p>
                  </div>
                  {tokenData.financialData.assumed_fdv && (
                    <div>
                      <label className="text-sm text-gray-400">Assumed FDV</label>
                      <p className="text-white">${tokenData.financialData.assumed_fdv.toLocaleString()}</p>
                    </div>
                  )}
                  {tokenData.financialData.total_supply && (
                    <div>
                      <label className="text-sm text-gray-400">Total Supply</label>
                      <p className="text-white">{tokenData.financialData.total_supply.toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!tokenData.vestingSchedule && !tokenData.financialData && (
              <div className="text-center py-8">
                <Coins className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">No token data available for this project</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default TokenDataModal 